import { type FC, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import cn from 'classnames';

import { getIcon } from '@/common/lib/getIcon';
import { type ProductOfDayDataType } from '@/common/types/contract';
import { storageTextObj } from '@/components/helpers/ObjectsText';
import { ModalContext } from '@/components/Modals/Modal/config';
import {
  H1,
  StringSmall,
  StringXSmall,
} from '@/components/Typography/Typography';
import { MainSmButton } from '@/components/ui/Button/Button';
import { Picture } from '@/components/ui/Picture/Picture';
import UpgradeGreenArrowsAnimation from '@/components/ui/UpgradeGreenArrowsAnimation/UpgradeGreenArrowsAnimation';

import ProductOfDayClaimButton from '../../ProductOfDayClaimButton';

import styles from './styles.module.scss';

import greenWheelPng from '/img/contract/greenwheel.png';
import greenWheelWebp from '/img/contract/greenwheel.webp';
import purpleWheelPng from '/img/contract/purplewheel.png';
import purpleWheelWebp from '/img/contract/purplewheel.webp';
import contractPng from '/img/icons/contract.png';
import contractWebp from '/img/icons/contract.webp';
import starPng from '/img/icons/star.png';
import starWebp from '/img/icons/star.webp';

interface ProductOfDayDefaultScreenProps {
  product: ProductOfDayDataType;
  onUpgrade: () => void;
}

const ProductOfDayDefaultScreen: FC<ProductOfDayDefaultScreenProps> = ({
  product,
  onUpgrade,
}) => {
  const { t } = useTranslation();
  const image = getIcon(product.name);
  const percent =
    (Math.min(product.progress, 20) / product.countToReward) * 100;
  const { secondModal } = useContext(ModalContext);
  const availableForUpgrade =
    product.type !== 'premium' && !product.chestBoxOpenedAt;

  return (
    <div className="flex flex-col items-center px-6">
      <div className="flex flex-col text-center gap-1">
        <H1 weight="bold">{t('Daily Goods')}</H1>
        <StringSmall weight="medium" className="!text-white/60">
          {storageTextObj(t)[product.name]}
        </StringSmall>
      </div>
      <div className="relative size-40 z-10">
        <Picture
          className="size-full"
          img={image?.png}
          webp={image?.webp}
          alt="product icon"
        />
        {availableForUpgrade && (
          <UpgradeGreenArrowsAnimation className="absolute right-0 top-8 z-[-1] w-12" />
        )}
      </div>

      <div
        className={cn(
          'flex gap-2 pl-3  rounded-full bg-white/10 -mt-4 z-10 relative h-8',
          !availableForUpgrade && 'pr-3',
        )}
      >
        <div className="flex gap-0.5 items-center">
          <span className="text-[1.5rem] font-extrabold text-gold leading-none">
            +{product.extraStarPoints}
          </span>
          <Picture
            className="size-6"
            img={starPng}
            webp={starWebp}
            alt="star"
          />
        </div>
        {availableForUpgrade && (
          <MainSmButton
            onClick={onUpgrade}
            text={t('Upgrade')}
            className="h-8 !rounded-full px-4"
          />
        )}
      </div>

      <div className="pl-6 flex gap-4 rounded-full bg-white/5 items-center mt-6 h-16 pr-[5.5rem] relative self-stretch mr-4">
        <div className="flex flex-col gap-2 w-full">
          <div className="flex items-center justify-between">
            <StringSmall>{t('Contracts')}</StringSmall>
            <div className="flex gap-0.5 text-[0.875rem] font-bold text-white/40">
              <span className="text-white">{product.progress}</span>
              <span>/</span>
              <span>{product.countToReward}</span>
            </div>
          </div>
          <div className="relative flex h-3 rounded-2xl bg-main-bg">
            <div
              className={cn(styles.progress, {
                [styles.purple]: product.type === 'premium',
              })}
              style={{
                width: percent + '%',
                border: percent > 0 ? '' : 'none',
              }}
            />
            <Picture
              className="size-6 absolute top-1/2 -translate-y-1/2"
              img={contractPng}
              webp={contractWebp}
              alt="contract"
              style={{
                left: `calc(${percent}% - 1rem)`,
              }}
            />
          </div>
        </div>
        <Picture
          className="size-[5.5rem] top-1/2 -translate-y-1/2 absolute -right-4 scale-x-[-1]"
          img={product.type === 'premium' ? purpleWheelPng : greenWheelPng}
          webp={product.type === 'premium' ? purpleWheelWebp : greenWheelWebp}
          alt="box"
          onClick={() => {
            if (percent < 100)
              return secondModal.openModal({
                renderKind: 'productOfDaySpin',
                props: { productId: product.id },
              });

            if (availableForUpgrade) onUpgrade();
          }}
        />
      </div>

      <div className="mt-6 flex flex-col text-center">
        <StringXSmall className="!leading-[120%] !text-white/60">
          <Trans
            i18nKey="Complete 7 contracts featuring “Daily Goods” <br /> to get a chance to win prizes"
            components={{ br: <br /> }}
          />
        </StringXSmall>
      </div>

      <ProductOfDayClaimButton
        product={product}
        className="self-stretch mt-6 -mx-2"
      />
    </div>
  );
};

export default ProductOfDayDefaultScreen;
